import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import api from "../../config/URL";
import { toast } from "react-toastify";
import fetchAllStudentListByCenter from "../List/StudentListByCenter";
import fetchOverAllStudentCompletionForm from "../List/StudentAllCompletedList";
import Select from "react-select";
import CustomDatePicker from "../CustomDatePicker";

function PaymentsAdd({ selectedCenter }) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const invoiceId = queryParams.get("invoiceId");
  const student_Id = queryParams.get("student");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const navigate = useNavigate();
  const [loadIndicator, setLoadIndicator] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [invoiceData, setInvoiceData] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);
  const [creditCardFee, setCreditCardFee] = useState(0);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [invoiceError, setInvoiceError] = useState("");

  const validationSchema = Yup.object({
    studentId: Yup.string().required("Student Name is required"),
    paymentDate: Yup.string()
      .required("Payment Date is required")
      .test(
        "paymentDate-check",
        "Payment Date cannot be earlier than Invoice Date",
        function (value) {
          const { invoiceIds } = this.parent;
          if (invoiceIds.length > 0) {
            const earliestInvoice = invoiceData.reduce((earliest, invoice) => {
              const invoiceDate = new Date(invoice.invoiceDate);
              return invoiceDate < earliest ? invoiceDate : earliest;
            }, new Date());

            const paymentDate = new Date(value);
            if (paymentDate < earliestInvoice) {
              return this.createError({
                message: "Payment Date cannot be earlier than Invoice Date",
                path: "paymentDate",
              });
            }
          }
          return true;
        }
      ),
    paymentMethod: Yup.string().required("Payment Method is required"),
    bank: Yup.string()
      .nullable()
      .trim()
      .when("paymentMethod", {
        is: (val) => val === "Credit Card" || val === "Internet Banking",
        then: (schema) => schema.required("Bank Name is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    transactionNo: Yup.string()
      .nullable()
      .trim()
      .when("paymentMethod", {
        is: "Internet Banking",
        then: (schema) => schema.required("Transaction No is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    mobileNumber: Yup.string()
      .matches(/^\d{8}$/, "Mobile Number must be 8 digits")
      .nullable()
      .when("paymentMethod", {
        is: "Internet Banking",
        then: (schema) => schema.required("Mobile Number is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    file: Yup.mixed().notRequired(),
    remark: Yup.string().notRequired(),
  });

  const formik = useFormik({
    initialValues: {
      centerId: selectedCenter,
      studentId: student_Id || "",
      paymentDate: new Date().toISOString().split("T")[0], // Set default to current date
      paymentMethod: "",
      paymentReference: "",
      paidAmount: "0",
      accountNo: "",
      bank: "",
      transactionNo: "",
      mobileNumber: "",
      file: null,
      remark: "",
      invoiceIds: [],
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (selectedInvoices.length === 0) {
        setInvoiceError("Please select an invoice");
        return;
      }
      setLoadIndicator(true);
      const formData = new FormData();
      formData.append("centerId", selectedCenter);
      formData.append("studentId", values.studentId);
      formData.append("paymentDate", values.paymentDate);
      formData.append("paymentMethod", values.paymentMethod);
      formData.append("paymentReference", values.paymentReference);
      const finalPaidAmount =
        values.paymentMethod === "Credit Card"
          ? parseFloat(values.paidAmount) + creditCardFee
          : parseFloat(values.paidAmount);
      formData.append("paidAmount", finalPaidAmount.toFixed(2));
      formData.append("bank", values.bank);
      formData.append("transactionNo", values.transactionNo);
      formData.append("accountNo", creditCardFee);
      formData.append("mobileNumber", values.mobileNumber);
      formData.append("paymentStatus", "PAID");
      formData.append("remark", values.remark);

      if (values.file) {
        formData.append("file", values.file);
      }

      const allocations = selectedInvoices.map((invoice) => ({
        invoiceId: invoice.id,
        receiptAmount: parseFloat(
          invoice.amountToPay || invoice.outstandingAmount
        ),
      }));
      formData.append("allocations", JSON.stringify(allocations));

      try {
        const response = await api.post("/createPayment", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (response.status === 201) {
          toast.success(response.data.message);
          navigate("/payment");
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to create payment"
        );
      } finally {
        setLoadIndicator(false);
      }
    },
    validateOnChange: false,
    validateOnBlur: true,
  });

  const scrollToError = (errors) => {
    const errorField = Object.keys(errors)[0];
    const errorElement = document.querySelector(`[name="${errorField}"]`);
    if (errorElement) {
      errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      errorElement.focus();
    }
  };

  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      scrollToError(formik.errors);
    }
  }, [formik.submitCount, formik.errors]);

  const fetchStudent = async (centerId) => {
    try {
      let student = [] || "";
      const numericCenterId = Number(centerId);
      if (numericCenterId === 0) {
        student = await fetchOverAllStudentCompletionForm();
      } else {
        student = await fetchAllStudentListByCenter(numericCenterId);
      }
      if (!Array.isArray(student)) {
        throw new Error("API did not return an array");
      }
      const formattedStudent = student.map((std) => ({
        value: std.id,
        label: std.studentNames,
      }));
      setStudentOptions(formattedStudent);
      setStudentData(student);

      if (student_Id) {
        const selectedStudent = formattedStudent.find(
          (option) => option.value === parseInt(student_Id)
        );
        if (selectedStudent) {
          formik.setFieldValue("studentId", selectedStudent.value);
          handleStudentChange(selectedStudent);
        }
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error(error.message || "Failed to fetch students");
    }
  };

  const handleStudentChange = async (selectedOption) => {
    formik.setFieldValue(
      "studentId",
      selectedOption ? selectedOption.value : ""
    );
    formik.setFieldValue("paidAmount", "0");
    setSelectedInvoices([]);
    setInvoiceData([]);
    setCreditCardFee(0);
    setInvoiceError("");
    if (selectedOption) {
      try {
        const response = await api.get(
          `/paymentInvoiceList?centerId=${selectedCenter}&studentId=${selectedOption.value}`
        );
        setInvoiceData(response.data);
      } catch (error) {
        toast.error(error.message || "Error fetching invoices");
      }
    }
  };

  const handleInvoiceSelection = (id) => {
    const invoice = invoiceData.find((inv) => inv.id === id);
    if (invoice) {
      const isSelected = selectedInvoices.some((inv) => inv.id === id);
      if (isSelected) {
        setSelectedInvoices(selectedInvoices.filter((inv) => inv.id !== id));
      } else {
        setSelectedInvoices([
          ...selectedInvoices,
          { ...invoice, amountToPay: invoice.outstandingAmount },
        ]);
      }
      setInvoiceError("");
    }
  };

  const handleAmountChange = (id, value) => {
    const updatedInvoices = selectedInvoices.map((inv) =>
      inv.id === id ? { ...inv, amountToPay: value } : inv
    );
    setSelectedInvoices(updatedInvoices);
  };

  useEffect(() => {
    const totalPaidAmount = selectedInvoices.reduce(
      (sum, invoice) => sum + (parseFloat(invoice.amountToPay) || 0),
      0
    );
    formik.setFieldValue("paidAmount", totalPaidAmount.toFixed(2));
    if (formik.values.paymentMethod === "Credit Card") {
      const fee = totalPaidAmount * 0.03;
      setCreditCardFee(fee);
    }
  }, [selectedInvoices, formik.values.paymentMethod]);

  useEffect(() => {
    formik.setFieldValue("studentId", student_Id || "");
    formik.setFieldValue("paidAmount", "0");
    setCreditCardFee(0);
    setStudentOptions([]);
    setStudentData(null);
    setInvoiceData([]);
    setSelectedInvoices([]);
    setInvoiceError("");
    fetchStudent(selectedCenter);
  }, [selectedCenter]);

  const handlePaymethodChange = (event) => {
    const paymentMethod = event.target.value;
    formik.setFieldValue("paymentMethod", paymentMethod);
    formik.setFieldValue("bank", "");
    formik.setFieldValue("transactionNo", "");
    formik.setFieldValue("mobileNumber", "");
    if (paymentMethod === "Credit Card") {
      const baseAmount = parseFloat(formik.values.paidAmount) || 0;
      const fee = baseAmount * 0.03;
      setCreditCardFee(fee);
    } else {
      setCreditCardFee(0);
    }
  };

  return (
    <div className="container-fluid">
      <ol
        className="breadcrumb my-3 px-2"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Invoice and Payment
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          <Link to="/payments" className="custom-breadcrumb">
            Payment
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          Payment Add
        </li>
      </ol>
      <form onSubmit={formik.handleSubmit}>
        <div className="card">
          <div
            className="d-flex justify-content-between align-items-center p-1 mb-4 px-4"
            style={{ background: "#f5f7f9" }}
          >
            <div className="d-flex align-items-center">
              <div className="d-flex">
                <div className="dot active"></div>
              </div>
              <span className="me-2 text-muted">Add Payment</span>
            </div>
            <div className="my-2 pe-3 d-flex align-items-center">
              <Link to="/payment">
                <button type="button" className="btn btn-sm btn-border">
                  Back
                </button>
              </Link>
                
              <button
                type="submit"
                className="btn btn-button btn-sm"
                disabled={loadIndicator}
              >
                {loadIndicator && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                  ></span>
                )}
                <span className="fw-medium">Save</span>
              </button>
            </div>
          </div>
          <div className="container-fluid px-4">
            <div className="row">
              <div className="col-md-6 col-12 mb-2">
                <label className="form-label">
                  Student Name<span className="text-danger">*</span>
                </label>
                <Select
                  options={studentOptions}
                  name="studentId"
                  value={
                    studentOptions.find(
                      (option) => option.value === formik.values.studentId
                    ) || null
                  }
                  onChange={handleStudentChange}
                  placeholder="Select Student"
                  isSearchable
                  isClearable
                  className={`form-multi-select ${
                    formik.touched.studentId && formik.errors.studentId
                      ? "is-invalid"
                      : ""
                  }`}
                />
                {formik.touched.studentId && formik.errors.studentId && (
                  <div className="invalid-feedback">
                    {formik.errors.studentId}
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">
                    Payment Date<span className="text-danger">*</span>
                  </label>
                  <CustomDatePicker
                    dateFormat="yyyy-MM-dd"
                    className="form-control center_list ps-5"
                    name="paymentDate"
                    value={formik.values.paymentDate}
                    onChange={(date) => {
                      const formattedDate = new Date(date)
                        .toISOString()
                        .split("T")[0];
                      formik.setFieldValue("paymentDate", formattedDate);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.errors.paymentDate}
                    touched={formik.touched.paymentDate}
                  />
                  {formik.touched.paymentDate && formik.errors.paymentDate && (
                    <div className="error text-danger">
                      {formik.errors.paymentDate}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">
                    Payment Method<span className="text-danger">*</span>
                  </label>
                  <select
                    {...formik.getFieldProps("paymentMethod")}
                    name="paymentMethod"
                    className={`form-select ${
                      formik.touched.paymentMethod &&
                      formik.errors.paymentMethod
                        ? "is-invalid"
                        : ""
                    }`}
                    onChange={handlePaymethodChange}
                  >
                    <option disabled value=""></option>
                    <option value="Cash">Cash</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Internet Banking">Internet Banking</option>
                    <option value="Paynow">Paynow</option>
                    <option value="Paynow QR">Paynow QR</option>
                  </select>
                  {formik.touched.paymentMethod &&
                    formik.errors.paymentMethod && (
                      <div className="invalid-feedback">
                        {formik.errors.paymentMethod}
                      </div>
                    )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">Payment Reference</label>
                  <input
                    name="paymentReference"
                    {...formik.getFieldProps("paymentReference")}
                    type="text"
                    className={`form-control ${
                      formik.touched.paymentReference &&
                      formik.errors.paymentReference
                        ? "is-invalid"
                        : ""
                    }`}
                    placeholder=""
                  />
                </div>
              </div>
              {(formik.values.paymentMethod === "Credit Card" ||
                formik.values.paymentMethod === "Internet Banking") && (
                <>
                  <div className="col-md-6 col-12 mb-2">
                    <label className="form-label">
                      Bank<span className="text-danger">*</span>
                    </label>
                    <input
                      name="bank"
                      {...formik.getFieldProps("bank")}
                      type="text"
                      className={`form-control ${
                        formik.touched.bank && formik.errors.bank
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    {formik.touched.bank && formik.errors.bank && (
                      <div className="invalid-feedback">
                        {formik.errors.bank}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6 col-12 mb-2">
                    <label className="form-label">Upload File</label>
                    <input
                      type="file"
                      name="file"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                      className="form-control"
                      onChange={(event) => {
                        formik.setFieldValue("file", event.target.files[0]);
                      }}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                </>
              )}
              {formik.values.paymentMethod === "Credit Card" && (
                <div className="col-md-6 col-12 mb-2">
                  <label className="form-label">Credit Card Charges (3%)</label>
                  <input
                    name="accountNo"
                    value={creditCardFee.toFixed(2)}
                    type="text"
                    className="form-control"
                  />
                </div>
              )}
              {formik.values.paymentMethod === "Internet Banking" && (
                <>
                  <div className="col-md-6 col-12 mb-2">
                    <label className="form-label">Transaction No</label>
                    <input
                      name="transactionNo"
                      {...formik.getFieldProps("transactionNo")}
                      type="text"
                      className={`form-control ${
                        formik.touched.transactionNo &&
                        formik.errors.transactionNo
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    {formik.touched.transactionNo &&
                      formik.errors.transactionNo && (
                        <div className="invalid-feedback">
                          {formik.errors.transactionNo}
                        </div>
                      )}
                  </div>
                  <div className="col-md-6 col-12 mb-2">
                    <label className="form-label">Mobile Number</label>
                    <input
                      name="mobileNumber"
                      {...formik.getFieldProps("mobileNumber")}
                      type="text"
                      className={`form-control ${
                        formik.touched.mobileNumber &&
                        formik.errors.mobileNumber
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    {formik.touched.mobileNumber &&
                      formik.errors.mobileNumber && (
                        <div className="invalid-feedback">
                          {formik.errors.mobileNumber}
                        </div>
                      )}
                  </div>
                </>
              )}
              <div className="col-md-6 col-12 mb-2">
                <div className="mb-3">
                  <label className="form-label">Paid Amount</label>
                  <input
                    name="paidAmount"
                    value={
                      formik.values.paymentMethod === "Credit Card"
                        ? (
                            parseFloat(formik.values.paidAmount || 0) +
                            creditCardFee
                          ).toFixed(2)
                        : formik.values.paidAmount
                    }
                    type="text"
                    className={`form-control ${
                      formik.touched.paidAmount && formik.errors.paidAmount
                        ? "is-invalid"
                        : ""
                    }`}
                    readOnly
                  />
                  {formik.touched.paidAmount && formik.errors.paidAmount && (
                    <div className="invalid-feedback">
                      {formik.errors.paidAmount}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12">
                <label className="form-label">Remark</label>
                <div className="input-group mb-3">
                  <textarea
                    name="remark"
                    className="form-control"
                    {...formik.getFieldProps("remark")}
                    id="remark"
                    rows="3"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
          <div className="container-fluid px-4 my-2">
            <div className="d-flex justify-content-start mb-2">
              {invoiceError && (
                <div className="text-danger">{invoiceError}</div>
              )}
            </div>
            <div className="table-responsive payment-table">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Bill To</th>
                    <th>Invoice No.</th>
                    <th>Invoice Date</th>
                    <th>Payment Due Date</th>
                    <th>Course Name</th>
                    <th>Total Amount</th>
                    <th>Outstanding Amount</th>
                    <th>Amount to Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceData.map((invoice, index) => (
                    <tr key={invoice.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedInvoices.some(
                            (inv) => inv.id === invoice.id
                          )}
                          onChange={() => handleInvoiceSelection(invoice.id)}
                        />
                      </td>
                      <td>{invoice.studentName}</td>
                      <td>{invoice.invoiceNumber}</td>
                      <td>
                        {new Date(invoice.invoiceDate).toLocaleDateString()}
                      </td>
                      <td>{new Date(invoice.dueDate).toLocaleDateString()}</td>
                      <td>{invoice.courseName}</td>
                      <td>{invoice.totalAmount}</td>
                      <td>{invoice.outstandingAmount}</td>
                      <td>
                        {selectedInvoices.some(
                          (inv) => inv.id === invoice.id
                        ) && (
                          <input
                            type="text"
                            value={
                              selectedInvoices.find(
                                (inv) => inv.id === invoice.id
                              )?.amountToPay || invoice.outstandingAmount
                            }
                            onChange={(e) =>
                              handleAmountChange(invoice.id, e.target.value)
                            }
                            className="form-control"
                            min="0"
                            max={invoice.outstandingAmount}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="row">
              <div className="col-8">
                <div className="text-end ms-5">Total Amount :</div>
              </div>
              <div className="col-4">
                <div className="text-start ">
                  {invoiceData
                    .reduce(
                      (sum, invoice) => sum + (invoice.totalAmount || 0),
                      0
                    )
                    .toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default PaymentsAdd;